import { OptionType } from 'components';

export enum WorkedHours {
    Plus200 = 'PT200H',
    Minus200 = '-PT200H',
    Plus800 = 'PT800H',
    Minus800 = '-PT800H',
}

export const workedHoursS3201Options: OptionType[] = [
    { value: WorkedHours.Plus200, label: 'Cas général +200H' },
    { value: WorkedHours.Minus200, label: 'Cas général -200H' },
    { value: WorkedHours.Plus800, label: 'Cas particulier +800H' },
    { value: WorkedHours.Minus800, label: 'Cas particulier -800H' },
];

export const workedHoursS3202800Options: OptionType[] = [
    { value: WorkedHours.Plus800, label: '+800H' },
    { value: WorkedHours.Minus800, label: '-800H' },
];

export const workedHoursS3202200ptions: OptionType[] = [
    { value: WorkedHours.Plus200, label: '+200H' },
    { value: WorkedHours.Minus200, label: '-200H' },
];

export enum WorkStoppagePeriods {
    Plus3Months = 'P3M',
    NinetyDays = '90days',
    sixMonths = 'P6M',
    twelveMonths = 'P12M',
    OneYear = '365days',
}

export const workStoppagePeriodOptions: OptionType[] = [
    { value: WorkStoppagePeriods.Plus3Months, label: '+3 mois civil' },
    { value: WorkStoppagePeriods.NinetyDays, label: '90 jours' },
    { value: WorkStoppagePeriods.sixMonths, label: '6 mois civil' },
    { value: WorkStoppagePeriods.twelveMonths, label: '12 mois civil' },
    { value: WorkStoppagePeriods.OneYear, label: '365 jours' },
];
export enum TrueFalse {
    True = 'true',
    False = 'false',
}
// Array of Oui/Non options

export const ouiNonOptions: OptionType[] = [
    { value: TrueFalse.True, label: 'OUI' },
    { value: TrueFalse.False, label: 'NON' },
];

/**
 * Returns an array of eligibility case values based on the provided category code (codCategorie).
 * If the category code matches specific exceptions ('APP' or 'STD'), only the 'Plus200' and 'Minus200'
 * eligibility cases are returned. Otherwise, it returns all possible values from the WorkedHours enum.
 *
 * @param {string} codCategorie - The category code to determine which eligibility cases to return.
 * @returns {WorkedHours[]} - An array of eligibility case values based on the category code.
 */
export const workedHoursValuesByCategorie = (codCategorie: string): WorkedHours[] => {
    const isExceptionCategory = codCategorie === 'APP' || codCategorie === 'STD';
    return isExceptionCategory ? [WorkedHours.Plus200, WorkedHours.Minus200] : Object.values(WorkedHours);
};

/**
 * Filters the list of eligibility case options based on the provided category code (codCategorie).
 *
 * @param {string} codCategorie - The affiliate job's category code used to filter the eligibility case options.
 * @returns {OptionType<WorkedHours>[]} - A filtered list of eligibility case options based on the category.
 */
export const filterWorkedHoursOptions = (codCategorie: string): OptionType<WorkedHours>[] => {
    const availableCases = workedHoursValuesByCategorie(codCategorie);
    return workedHoursS3201Options.filter((option): option is OptionType<WorkedHours> => availableCases.includes(option.value as WorkedHours));
};

/**
 * Recursively fetch salary rights for all eligibility cases based on the category code.
 * @param {string} codCategorie - The category code used to determine eligibility cases.
 * @returns {WorkStoppagePeriods[]} - A list of salary rights corresponding to the eligibility cases.
 */
export const recursivePeriodsByCategorie = (codCategorie: string): WorkStoppagePeriods[] => {
    // Get the list of eligibility cases based on the category
    const workedHourss = workedHoursValuesByCategorie(codCategorie);

    // Initialize an empty array to store salary rights
    let allPeriods: WorkStoppagePeriods[] = [];

    // Loop through each eligibility case and recursively get salary rights
    workedHourss.forEach((workedHours) => {
        const PeriodsForCase = PeriodValuesByWorkedHours(workedHours, codCategorie);
        allPeriods = [...allPeriods, ...PeriodsForCase];
    });

    // Return the aggregated list of salary rights, ensuring no duplicates
    return Array.from(new Set(allPeriods));
};

/**
 * Get salary rights Values by case
 * @param {string} [value]
 * @returns {WorkStoppagePeriods[]}
 */
export const PeriodValuesByWorkedHours = (workedHours: string, codCategorie: string): WorkStoppagePeriods[] => {
    let values = [];
    switch (workedHours) {
        case WorkedHours.Plus200:
            values = workStoppagePeriodOptions.filter((option) => option.value === WorkStoppagePeriods.NinetyDays || option.value === WorkStoppagePeriods.Plus3Months);
            break;
        case WorkedHours.Minus200:
            values = workStoppagePeriodOptions.filter((option) => option.value === WorkStoppagePeriods.sixMonths);
            break;
        case WorkedHours.Plus800:
            values = workStoppagePeriodOptions.filter((option) => option.value === WorkStoppagePeriods.twelveMonths || option.value === WorkStoppagePeriods.OneYear);
            break;
        case WorkedHours.Minus800:
            values = workStoppagePeriodOptions.filter((option) => option.value === WorkStoppagePeriods.OneYear);
            break;
        default:
            return recursivePeriodsByCategorie(codCategorie);
    }

    return values.map((o) => o.value as WorkStoppagePeriods);
};

export const filterPeriodsOptionsByWorkedHours = (workedHours: string, codCategorie: string): OptionType<WorkStoppagePeriods>[] => {
    const availableOptions = PeriodValuesByWorkedHours(workedHours, codCategorie);

    return workStoppagePeriodOptions.filter((option): option is OptionType<WorkStoppagePeriods> => availableOptions.includes(option.value as WorkStoppagePeriods));
};
